import {Component, EventEmitter, forwardRef, Input, Output, TemplateRef, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {ColDef, ColType} from "../../interfaces/colDef";
import {EditDialogComponent, EditType} from "../../dialogs/edit-dialog/edit-dialog.component";
import {NbDialogService} from "@nebular/theme";
import {TranslateService} from "@ngx-translate/core";
import {DualAxisDelegate, Editable} from "../../dual-axis/dual-axis.component";
import {TreeNode} from "../../interfaces/treenode";
import {Paged} from "../../interfaces/paged";
import {Globals} from "../../shared/Globals";
import {NotifierService} from "../../shared/notifier.service";

@Component({
    selector: 'bst-table-editor',
    templateUrl: './table-editor.component.html',
    styleUrls: ['./table-editor.component.scss', '../../directories/directory/directory.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TableEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class TableEditorComponent<T> extends SubscribedComponent implements ControlValueAccessor, DualAxisDelegate<T>{

  @Input() title: string = null;
  @Input() subtitle: string = null;
  @Input() autoopen: T = null;
  @Input() autoadd: boolean = false;
  @Input() standardColumns: ColDef[] = [];
  @Input() stickyColumnsLeft: ColDef[] = [];
  @Input() stickyColumnsRight: ColDef[] = [];
  @Input() couldEditRow: (row: T) => boolean = null;
  @Input() couldEditItem: (col: ColDef, items: T, item: any) => boolean = null;
  @Input() couldDeleteRow: (row: T) => boolean = null;

  @Output() onAdd = new EventEmitter<T>();
  @Output() onEdit = new EventEmitter<T>();
  @Output() onDelete = new EventEmitter<T>();


  Globals = Globals;
  editable: Editable<T>;

  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;
  public data: TreeNode<T>[] = [];

  public canEdit: boolean = true;
  public canAdd: boolean = true;
  public canDelete: boolean = true;
  public disabled:boolean = false;
  public ColType = ColType;
  public sorter: (a: TreeNode<T>, b: TreeNode<T>) => number = null;

  onChange = (value: T[]) => {};
  onTouched = () => {};

  constructor(private dialogService: NbDialogService,
              private notifier: NotifierService,
              private translate: TranslateService) {
    super();
  }

  private refreshEditable(){
    this.editable = null;
    if(!this.disabled && (this.canEdit || this.canDelete)){
      this.editable = {};
      if(this.canEdit){
        this.editable.edit = (row: TreeNode<T>) => {
          return this.editItem(row);
        };

        if(this.couldEditRow){
          this.editable.canEdit = (row: TreeNode<T>) => {
            return this.couldEditRow(row.data);
          };
        }
      }
      if(this.canDelete){
        this.editable.delete = (row: TreeNode<T>) => {
          return this.deleteItem(row);
        };

        if(this.couldDeleteRow){
          this.editable.canDelete = (row: TreeNode<T>) => {
            return this.couldDeleteRow(row.data);
          }
        }
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.refreshEditable();
  }

  writeValue(value: T[]): void {
    if(value){
      this.data = value.map(o => this.toNode(o));
    }
    this.onChange(this.values);

    if(this.data && this.autoopen && !this.autoadd){
      const opener = this.data.find(o => o.data === this.autoopen);
      if(opener){
        this.editItem(opener);
      }
    }
    else if(this.autoadd){
      this.newItem();
    }
  }

  get values(){
    if(this.data && this.data.length){
      return this.data.map(o => o.data);
    }
    return null;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.refreshEditable();
  }

  private updateitem(row: TreeNode<T>, newitem: T){
    const index = this.data.indexOf(row);
    if(index !== -1) {
      this.data[index] = this.toNode(newitem);
      this.onChange(this.values);
    }
  }

  private toNode(value: T){
    const node:TreeNode<T> = {
      data: value,
      expanded: false,
      children: []
    };
    return node;
  }

  newItem() {
    this.subs.push(this.dialogService.open<EditDialogComponent<T>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: this.title,
        description: this.standardColumns,
        type: EditType.ADD,
        data: null,
        canEditColumn: (col: ColDef, items: T, value: any) => {
          return !this.couldEditItem || this.couldEditItem(col, items, value);
        }
      }
    }).onClose.subscribe((choice: T) => {
      if(choice){
        this.onAdd.emit(choice);
        this.data.push(this.toNode(choice));
        this.onChange(this.values);
      }
    }));
  }

  editItem(row: TreeNode<T>) {
    this.subs.push(this.dialogService.open<EditDialogComponent<T>>(EditDialogComponent, {
      autoFocus: true,
      context: {
        title: this.title,
        description: this.standardColumns,
        data: row.data,
        canEditColumn: (col: ColDef, items: T, value: any) => {
          return !this.couldEditItem || this.couldEditItem(col, items, value);
        }
      }
    }).onClose.subscribe((choice: T) => {
      if(choice){
        this.onEdit.emit(choice);
        this.updateitem(row, choice);
      }
    }));
  }

  async deleteItem(row: TreeNode<T>) {
    if(!this.couldDeleteRow || this.couldDeleteRow(row.data)) {
      if(await this.notifier.confirm('deletechecklight', null, 'deleteforever', 'danger')){
        try {
          this.data = this.data.filter(o => o !== row);
          this.onDelete.emit(row.data);
          this.onChange(this.values);
        } catch (error) {
          console.error(error);
        }
      }
    }
  }

  displayFor(rowElement: any, column: ColDef) {
    const el = column.list.find(o => o.id === rowElement);
    if(el){
      return el.name;
    }
    return null;
  }

  paged(page: number): Promise<Paged<T>> {
    return Promise.resolve(undefined);
  }

  isTemplate(column: ColDef) {
    return column.display && column.display instanceof TemplateRef;
  }
}
